/**
 * 文案框的「推荐输入」跟着 目的/拍法 走(0824 用户实测:页内换了目的、选了拍法,
 * 文案框的引导一动不动)。
 *
 * ═══ 推荐输入到底该根据什么变 ═══════════════════════════════════════════════════
 * ① **目的(briefPurpose)**:8 档各自的 `briefSeed`(briefPurpose.ts 单一源)本来就是
 *    按档写好的备料问卷(拍给谁看/主打卖点/想让观众做什么…)——此前它只在「从 hub
 *    目的卡进页」那一条路上写入(VideoCreatePage 的 scenePreset effect),页内点目的
 *    chip 走的是 applyPurpose,一个字都不会动文案框。修法:目的的唯一写入口统一做
 *    seed 置换。
 * ② **拍法(pickedFormulaKey)**:拍法卡的节拍(`beats`)里带 `{槽位}` 原文
 *    ({产品名}/{痛点1}/{数字}/{老结果}…,formulas.yaml b15 逐字下发)——这张卡要用到
 *    什么料,槽位就是清单本身。不改用户正文,以文案框下方 hint 的形式常驻提示。
 * ③ 蓝图(追爆):已有 blueprintBriefPlaceholder,优先级最高,不动。
 *
 * ═══ 置换纪律:只动「我们的」文字,不动用户的 ═══════════════════════════════════
 * 判据 = 框内文本(trim 后)为空,或与任何一档的 briefSeed 逐字相同(= 没被用户改过的
 * 模板)。用户改过一个字,换目的就只换引擎行为、不再碰正文 —— 静默覆盖用户输入是比
 * 不引导更糟的事。
 */
import { BRIEF_PURPOSES, briefPurposeOf } from "@/entities/scene-card/model/briefPurpose";

import { DEFAULT_BRIEF_DURATION } from "./effectiveDuration";

import type { VideoFormulaCardDto } from "../api/videoService";
import type { FormState } from "./videoCreateTypes";

/** 框内文本是不是「可置换」:空,或就是某一档没被动过的 briefSeed 模板。 */
export function isReplaceableBrief(text: string | null | undefined): boolean {
  const t = (text ?? "").trim();
  if (!t) return true;
  return BRIEF_PURPOSES.some((d) => d.briefSeed.trim() === t);
}

/**
 * 目的的唯一写入口(VideoCreatePage.applyPurpose 委派):storyMode 映射 + seed 置换。
 *
 * ★storyMode 映射改判(2026-09-13,老板拍板「架构师打开」):恒 true。
 * 旧映射「只有剧情微故事开」是 W2 重构前的语义(那时 storyMode=故事引擎+锁主角);
 * 重构后 ai 侧的架构师是**两级 LLM 的第一级**,按目的产三种架构(带货/获客=persuasion
 * 论证弧、公司=experience、品牌/剧情=story),不再只是「讲故事」。真单 5787265009665
 * 实锤:旧映射把带货单的架构师整个关掉(storyBible=null),口播只剩单趟直出的清单腔
 * (「理由一/理由二」)。锁主角(useProtagonistAnchor)仍只在剧情微故事开——那才是
 * 故事专属。海报态不走这里(fromPoster 分支的 storyMode 语义不同)。
 * seed 置换:框内是空/未动过的模板 → 换成新档的 briefSeed;清目的(null=交给 AI 判)
 * → 模板一并清掉(留着上一档的问卷会误导 AI 判档)。
 */
export function applyPurposeToForm(s: FormState, label: string | null): FormState {
  const next: FormState = {
    ...s,
    briefPurpose: label,
    storyMode: true,
    useProtagonistAnchor: label === "剧情微故事",
    // B2 #11:选了目的而时长档**缺省**(undefined)→ 落「短 · 15s」;用户点过「AI 按内容定」是 null,不动。
    briefDuration:
      label != null && s.briefDuration === undefined ? DEFAULT_BRIEF_DURATION : s.briefDuration,
  };
  if (isReplaceableBrief(s.userInput)) {
    next.userInput = briefPurposeOf(label)?.briefSeed ?? "";
  }
  return next;
}

/**
 * 素材行按目的高亮的**副文案**(2026-09-12 目的锁定 A4)—— 目的锁定后备料台按目的定制的红利。
 *
 * ★只给「素材对成片影响大」的档一句引导,其余档返 null(不强加、不为凑齐而写废话):
 *   - 带货 / 硬广 / 剧情微故事:产品图是命根(EXP-R2V-2 实证不传图 → 各镜自己想象产品外观),
 *     一句「强烈建议传产品图」;
 *   - 招聘 / 公司介绍:出镜的人是重点,引到人物库锁一张脸(素材行现有人物位,支持)。
 * ★不新建素材项:logo 目前不是素材行的一个位(素材行只有产品图 + 人物),所以不提 logo ——
 *   「没有对应素材项就不强加」(拍板)。
 */
export function intakeMaterialHint(purpose?: string | null): string | null {
  const key = briefPurposeOf(purpose)?.key;
  if (key === "goods" || key === "hardsell" || key === "narrative") {
    return "带货 / 硬广 / 剧情片强烈建议传一张产品实拍图 —— 画面才贴你的真货,几个镜头之间外形也对得上。";
  }
  if (key === "recruit" || key === "company") {
    return "招聘 / 公司片建议从人物库锁一位出镜 —— 多个镜头认同一张脸,更像真人真企业。";
  }
  return null;
}

const _SLOT_RE = /\{([^{}|]{1,12})\}/g;

/** 拍法卡节拍里的 `{槽位}` 名(去重保序)—— 这张卡要用到什么料的权威清单。 */
export function formulaSlots(beats: readonly string[] | null | undefined): string[] {
  const out: string[] = [];
  for (const b of beats ?? []) {
    for (const m of b.matchAll(_SLOT_RE)) {
      const name = m[1].trim();
      if (name && !out.includes(name)) out.push(name);
    }
  }
  return out;
}

/**
 * 文案框下的拍法备料 hint;卡没有槽位(纯结构卡)返 null 不渲染。
 * 措辞口径:只提示不拦截(与资料文轻提示同一设计哲学);缺料不是错 —— AI 会替编,
 * 但给到真料镜头才贴自己的生意。
 */
export function formulaMaterialHint(
  card: Pick<VideoFormulaCardDto, "name" | "beats">
): string | null {
  const slots = formulaSlots(card.beats);
  if (slots.length === 0) return null;
  return `拍法「${card.name}」的节拍会用到:${slots.join("、")} —— 文案里把这些给到,镜头就按你的真料拍;缺的 AI 会替你编。`;
}

/**
 * 「想拍什么」空态 placeholder 的**拍法版**(2026-09-13 A3)。
 *
 * ★选了拍法时,占位提问按该拍法节拍的 {槽位} 组一句 —— 与 `formulaMaterialHint` 复用
 *   **同一套** `formulaSlots`(节拍里要用到什么料的权威清单),不另立第二份解析,漂移无处发生。
 * ★与 `formulaMaterialHint` 分工:那句是文案框**下方**常驻 hint(选了拍法就出);这句是文案框
 *   **框内**空态幽灵字(框空了才出),按拍法给一套提问方向,取代通用示例。
 * ★纯结构卡(无槽位)返 null,由上层回落到目的级 `briefPurpose.intakePlaceholder`(A3 口径)。
 */
export function formulaIntakePlaceholder(
  card: Pick<VideoFormulaCardDto, "name" | "beats">
): string | null {
  const slots = formulaSlots(card.beats);
  if (slots.length === 0) return null;
  return `按「${card.name}」这套拍法,把这些写进来:${slots.join("、")} —— 想到多少写多少,缺的 AI 会替你补。`;
}
